import React, { Fragment, ReactNode } from 'react';
import { makeStyles } from '@mui/styles';
import { grey } from '@mui/material/colors';
import { Box, Typography } from '@mui/material';
import { formatCurrency } from '../../../../helpers/stringFormatters';
import { generateEquipmentName } from '../../../../helpers/generateEquipmentName';

interface PublicEquipment {
  id: string;
  year: string;
  make: string;
  model: string;
  newUsed: string;
  mileage: number;
  classType: string;
  retailPrice: number;
  serialNumberVin: string;
}

interface Props {
  equipments: PublicEquipment[];
}

export const EquipmentInfo = ({ equipments }: Props) => {
  const classes = useStyles();

  const renderConfig: Partial<
    Record<keyof PublicEquipment, { label: string; formatter?: (value: any) => ReactNode }>
  > = {
    year: { label: 'Year' },
    make: { label: 'Make' },
    model: { label: 'Model' },
    mileage: { label: 'Mileage' },
    classType: { label: 'Class' },
    newUsed: { label: 'New Used' },
    serialNumberVin: { label: 'Serial/VIN#' },
    retailPrice: { label: 'Price', formatter: (retailPrice) => formatCurrency(retailPrice) },
  };

  return (
    <Box className={classes.container}>
      <Box className="equipmentHeader">
        <Typography fontWeight={500}>Equipment</Typography>
      </Box>

      <Box className="equipmentBody">
        {equipments.map((equipment) => (
          <Box key={equipment.id}>
            <Typography fontWeight={500}>{generateEquipmentName(equipment)}</Typography>

            <Box className="infoContainer">
              {Object.entries(equipment).map(([key, value]) => {
                const typedKey = key as keyof PublicEquipment;

                return (
                  <Fragment key={key}>
                    {!!renderConfig?.[typedKey] && (
                      <Box className="infoItem">
                        <Typography color={grey['600']} variant="subtitle2">
                          {renderConfig[typedKey]?.label}
                        </Typography>

                        <Typography variant="body1">
                          {typeof renderConfig[typedKey]?.formatter === 'function'
                            ? renderConfig[typedKey]?.formatter?.(value)
                            : value || '-'}
                        </Typography>
                      </Box>
                    )}
                  </Fragment>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '0.5rem',
    display: 'grid',
    marginTop: '1rem',
    gridTemplateRows: 'auto 1fr',

    '& .equipmentHeader': {
      padding: '8px 16px',
      backgroundColor: '#E8EAF6',
    },

    '& .equipmentBody': {
      gap: '1rem',
      display: 'flex',
      padding: '1rem 0.5rem',
      flexDirection: 'column',
      backgroundColor: grey['100'],

      '& .infoContainer': {
        display: 'grid',
        gap: '0.5rem 0',
        gridTemplateColumns: `repeat(4, 1fr)`,

        '& .infoItem': {
          borderBottom: `1px solid ${grey['300']}`,
          [`&:nth-last-child(-n+4)`]: {
            borderBottom: 'none',
          },
        },
      },
    },
  },
});
